import { useEffect } from "react";
import { usePostPropertyCottageMutation } from "web_myplann/Api/Portfolio/Mutations/usePostPropertyCottageMutation";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { BuildingFormInput } from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import { CottageFormFields } from "web_myplann/Portfolio/Forms/CottageForm/CottageFormFields";
import { COTTAGE_FORM_DEFAULT_VALUES } from "web_myplann/Portfolio/Forms/CottageForm/cottageFormDefaults";
import { getCottageFormSchema } from "web_myplann/Portfolio/Forms/CottageForm/cottageFormSchema";
import { RealEstateTypeRow } from "web_myplann/Portfolio/Forms/RealEstateForm/RealEstateTypeRow";
import type { RealEstateType } from "web_myplann/Portfolio/Forms/RealEstateForm/realEstateTypes";
import { PortfolioFormShell } from "web_myplann/Portfolio/PortfolioShared/PortfolioFormShell";
import { useCreatePortfolioForm } from "web_myplann/Portfolio/PortfolioShared/useCreatePortfolioForm";

type CreateCottageFormProps = {
	type: RealEstateType;
	onTypeChange: (type: RealEstateType) => void;
	registerSnapshot: (getValues: () => BuildingFormInput) => void;
	initialValues: BuildingFormInput | undefined;
};

export const CreateCottageForm = ({
	type,
	onTypeChange,
	registerSnapshot,
	initialValues,
}: CreateCottageFormProps) => {
	const { t } = useResource();

	const { mutate, isPending } = usePostPropertyCottageMutation();

	const { form, onSubmit } = useCreatePortfolioForm({
		schema: getCottageFormSchema(t),
		defaultValues: initialValues ?? COTTAGE_FORM_DEFAULT_VALUES,
		mutate,
		successRoute: Routing.Portfolio.PortfolioAddRealEstateSuccess.path(),
	});

	useEffect(() => {
		registerSnapshot(form.getValues);
	}, [registerSnapshot, form.getValues]);

	return (
		<PortfolioFormShell
			onSubmit={onSubmit}
			submitLabel={t(Resources.Portfolio.AddRealEstate.submit)}
			disabled={!form.formState.isDirty}
			loading={isPending}
		>
			<RealEstateTypeRow mode="select" value={type} onChange={onTypeChange} />
			<CottageFormFields control={form.control} />
		</PortfolioFormShell>
	);
};
